import { swSettings } from "app/common/services/swSettings";
import type { ICampaignsDataUnion } from "app/components/campaigns/shared/CampaignsConst";
import { campaignsFormatNb } from "app/components/campaigns/shared/CampaignsUtils";
import { translate } from "app/filters/i18nFilter";
import { apiClient } from "app/modules/api-client/apiClient";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignData,
    IKeywordsCampaignsCampaignRawData,
    IMyUserConsumptionMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { TabFilter } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { mapCampaignList } from "app/pages/digital-marketing/keywords-campaigns/infra/campaignMappers";
import { NoCacheHeaders } from "app/services/fetchService";

export const getIsRTCreateCampaignsAvailable = (): boolean => {
    return swSettings.components.PerformanceAddOn.resources.NumberTrackedKeywords > 0;
};

export const getAPIPrefixForRT = (isDemoCampaign: boolean): string => {
    return !isDemoCampaign ? "api/rankTracker" : "api/rankTracker/demo";
};

// Plain object: the generated client merges headers with Object.assign, which drops a Headers instance.
// NoCacheHeaders.Expires is numeric; HeadersInit values must be strings.
const rankTrackerListHeaders = { ...NoCacheHeaders, Expires: String(NoCacheHeaders.Expires) };

/** Demo campaign list rows (shared by homepage fetch and demo campaigns query). */
export const fetchDemoCampaignList = async (): Promise<IKeywordsCampaignsCampaignRawData[]> => {
    const demoCampaigns = await apiClient.RankTrackerDemoCampaigns.rtGetDemoCampaigns({
        headers: rankTrackerListHeaders,
    });
    return mapCampaignList(demoCampaigns, { isDemo: true, isShared: false });
};

export const fetchCampaigns = async (): Promise<IKeywordsCampaignsCampaignRawData[]> => {
    if (!getIsRTCreateCampaignsAvailable()) {
        return fetchDemoCampaignList();
    }

    // The list must not be cached: a campaign created or deleted in this session would stay hidden.
    const campaignConfigs = await apiClient.RankTracker.rtGetCampaigns({ headers: rankTrackerListHeaders });

    const userCampaigns = mapCampaignList(campaignConfigs.userCampaigns, { isShared: false }).map((campaign) => ({
        // Owned list rows stay editable on the homepage (owner can delete within 24h).
        ...campaign,
        isEditable: true,
    }));
    const sharedCampaigns = mapCampaignList(campaignConfigs.sharedCampaigns, { isShared: true });

    return [...userCampaigns, ...sharedCampaigns];
};

export const deleteCampaigns = async (campaignID: string): Promise<void> => {
    await apiClient.RankTracker.rtDeleteCampaign(campaignID);
};

export const doesCampaignMatchFilter = (campaign: IKeywordsCampaignData, tabIndex: number) =>
    ({
        [TabFilter.ALL]: !campaign.isDemo,
        [TabFilter.MY]: !campaign.isShared && !campaign.isDemo,
        [TabFilter.EDITABLE]: campaign.isEditable,
        [TabFilter.SHARED]: campaign.isShared,
        [TabFilter.DEMO]: campaign.isDemo,
    })[tabIndex] || false;

export const isCampaignActive = (campaign: IKeywordsCampaignData): boolean => !!campaign?.firstScrapeTime;

export const getMyUserConsumptionMetrics = (
    campaignsConfigurations: ICampaignsDataUnion,
): IMyUserConsumptionMetrics => {
    const myCampaignsIndex = campaignsConfigurations?.findIndex((campaign) => campaign.isShared);
    if (myCampaignsIndex === -1) {
        return {
            usedKeywords: 0,
            usedCampaigns: 0,
        };
    }
    const myCampaigns = campaignsConfigurations?.slice(0, myCampaignsIndex);
    const amountOfKeywords = myCampaigns?.reduce((acc, campaign) => {
        return acc + campaign.keywordCount;
    }, 0);
    return {
        usedKeywords: amountOfKeywords,
        usedCampaigns: myCampaigns?.length,
    };
};

export const getRankTrackerCampaignsAndKeywordsSubtitle = (
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics,
    myUserConsumptionMetrics: IMyUserConsumptionMetrics,
): string => {
    if (swSettings.user.isInternalUser) {
        return translate("seo.keywordsCampaign.campaigns.banner.balance.subTitle.internalUser", {
            usedCampaigns: campaignsFormatNb(myUserConsumptionMetrics?.usedCampaigns),
            usedKeywords: campaignsFormatNb(myUserConsumptionMetrics?.usedKeywords),
            maxKeywords: swSettings.components.RankTracker.resources.MaxKeywordInternalRankTracker,
        });
    }
    return translate("seo.keywordsCampaign.campaigns.banner.balance.subTitle", {
        usedCampaigns: campaignsFormatNb(campaignsConsumptionMetrics.usedCampaigns),
        totalCampaigns: campaignsFormatNb(campaignsConsumptionMetrics.totalCampaigns),
        usedKeywords: campaignsFormatNb(campaignsConsumptionMetrics.usedKeywords),
        totalKeywords: campaignsFormatNb(campaignsConsumptionMetrics.totalKeywords),
    });
};
